import { z } from 'zod';

export const REVIEW_MODES = ['quick', 'deep'] as const;
export const PLAN_ADHERENCE_VALUES = ['followed', 'drifted', 'abandoned'] as const;
export const DOMINANT_EMOTION_VALUES = [
  'conviction',
  'fomo',
  'revenge',
  'fear',
  'boredom',
  'neutral',
] as const;

const BaseTradeReviewSchema = z.object({
  planAdherence: z.enum(PLAN_ADHERENCE_VALUES),
  dominantEmotion: z.enum(DOMINANT_EMOTION_VALUES),
});

export const QuickTradeReviewSchema = BaseTradeReviewSchema.extend({
  mode: z.literal('quick'),
  oneLineLesson: z.string().trim().max(140).optional(),
});

export const DeepTradeReviewSchema = BaseTradeReviewSchema.extend({
  mode: z.literal('deep'),
  oneLineLesson: z.string().trim().max(140).optional(),
  whatHappened: z.string().trim().min(20).max(500),
  whatToChange: z.string().trim().min(20).max(500),
});

export const SubmitTradeReviewSchema = z.discriminatedUnion('mode', [
  QuickTradeReviewSchema,
  DeepTradeReviewSchema,
]);

export const WeeklyReviewNotesSchema = z.object({
  notes: z.string().max(20_000),
});

export const WeeklyReviewMetricsSchema = z.object({
  weekStart: z.string(),
  weekEnd: z.string(),
  headline: z.object({
    realisedPnl: z.string(),
    unrealisedPnl: z.string(),
    deltaNav: z.string().nullable(),
    drawdown: z.number(),
  }),
  closedLotsCount: z.number().int().nonnegative(),
  winRate: z.number().nullable(),
  expectancyR: z.number().nullable(),
  profitFactor: z.number().nullable(),
  rMultipleDistribution: z.array(
    z.object({ bucket: z.string(), count: z.number().int().nonnegative() }),
  ),
  topWinners: z.array(z.object({ symbol: z.string(), pnl: z.string() })),
  topLosers: z.array(z.object({ symbol: z.string(), pnl: z.string() })),
  adherence: z.object({
    preTradeEntries: z.number().int().nonnegative(),
    totalTrades: z.number().int().nonnegative(),
    rate: z.number().nullable(),
  }),
  singleBiggestIssue: z
    .object({ ruleKey: z.string(), count: z.number().int().positive() })
    .nullable(),
  lessons: z.array(z.string()),
});

export const WeeklyReviewFlagsSummarySchema = z.object({
  countsByKind: z.record(z.string(), z.number().int().nonnegative()),
  total: z.number().int().nonnegative(),
});

export type WeeklyReviewNotesInput = z.infer<typeof WeeklyReviewNotesSchema>;
export type WeeklyReviewMetrics = z.infer<typeof WeeklyReviewMetricsSchema>;
export type WeeklyReviewFlagsSummary = z.infer<typeof WeeklyReviewFlagsSummarySchema>;
export type ReviewMode = (typeof REVIEW_MODES)[number];
export type PlanAdherence = (typeof PLAN_ADHERENCE_VALUES)[number];
export type DominantEmotion = (typeof DOMINANT_EMOTION_VALUES)[number];
export type SubmitTradeReviewInput = z.infer<typeof SubmitTradeReviewSchema>;
